import React from "react";
import { Card } from "./UI";
import { fmtNum } from "../../utils/format";
import styles from "./AudiencePortraits.module.css";

// Портреты ЦА по трём продуктам: кураторское описание (по анализу реальных
// данных: signupSource, возраст/пол профилей, устройства ip_log, платежи)
// + живая демография из /api/segments/portraits.

const PORTRAITS = [
  {
    key: "rublox",
    title: "Рублокс",
    persona: "Школьник-геймер",
    color: "#4f7cff",
    who: [
      "Мальчик 10–15 лет (мужской пол — у 92% указавших). Увидел видео в TikTok или искал «рублокс» в поиске, зарегистрировался прямо на сайте с браузера — топ воронки и главный источник аудитории всей экосистемы.",
      "Играет в чужие игры, самые вовлечённые строят свои миры в студии. Продукт бесплатный: этот сегмент пока не платит, его ценность — переток в Маську и Майнкрафтию.",
    ],
    caveat: "Пики «11» и «26 лет» в данных — частично артефакт дефолтной даты рождения в форме регистрации.",
  },
  {
    key: "minecraftia",
    title: "Майнкрафтия",
    persona: "Родитель + ученик",
    color: "#3fb950",
    who: [
      "Покупатель и пользователь — разные люди. Платит родитель, занимается ребёнок 9–15 лет, ядро — 11–14. Средний платёж ≈3 300 ₽ — месяц курса: программирование, робототехника, математика.",
      "Онлайн-активные — школьники, решают задачи в личном кабинете. Трафик тёплый: поиск «майнкрафтия», «школа программирования майнкрафт», отказы всего ~2% — приходят осознанно, а не из любопытства.",
    ],
    caveat: "Онлайн-подписку (2 990 ₽) пока покупают единицы — родители 30+; основная выручка идёт через оплаты курсов.",
  },
  {
    key: "maska",
    title: "Маська",
    persona: "Подросток из комьюнити",
    color: "#e8445a",
    who: [
      "Подросток 12–17 лет, пришёл из Рублокса и TikTok. Четыре из пяти регистраций — с телефона (Android APK): это мобильная аудитория, живущая в чатах. Общается в группах и каналах, смотрит ленту, играет в HTML5-игры.",
      "Платящих пока единицы: покупают звёзды мелкими пакетами (59–149 ₽) — модель микроплатежей как в Telegram Stars. Потенциал монетизации — подарки, донаты авторам и бусты игр.",
    ],
    caveat: "Пики «12» и «26 лет» — частично дефолтный год рождения в онбординге.",
  },
];

function StatBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className={styles.barRow}>
      <span className={styles.barLabel}>{label}</span>
      <div className={styles.barTrack}>
        <div className={styles.barFill} style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className={styles.barValue}>{fmtNum(value)}</span>
    </div>
  );
}

function GenderLine({ gender }) {
  const known = (gender?.male || 0) + (gender?.female || 0);
  if (!known) return null;
  const malePct = Math.round((gender.male / known) * 100);
  return (
    <div className={styles.substat}>
      Пол (указали {fmtNum(known)}): <b>{malePct}% мальчики</b>, {100 - malePct}% девочки
    </div>
  );
}

function DevicesLine({ devices }) {
  const entries = Object.entries(devices || {}).filter(([k]) => k !== "unknown");
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (!total) return null;
  const parts = entries
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${{ web: "браузер", android: "Android", ios: "iOS" }[k] || k} ${Math.round((v / total) * 100)}%`);
  return <div className={styles.substat}>Устройства при регистрации: <b>{parts.join(" · ")}</b></div>;
}

export default function AudiencePortraits({ data }) {
  if (!data) return null;
  return (
    <div className={styles.grid}>
      {PORTRAITS.map((p) => {
        const live = data.products?.[p.key] || {};
        const demo = live.demo || {};
        const sizes = live.sizes || {};
        const ages = demo.ages || {};
        const maxAge = Math.max(1, ...Object.values(ages));
        return (
          <Card key={p.key}>
            <div className={styles.head}>
              <span className={styles.dot} style={{ background: p.color }} />
              <div>
                <div className={styles.product}>{p.title}</div>
                <div className={styles.persona}>{p.persona}</div>
              </div>
            </div>

            {p.who.map((text, i) => (
              <p key={i} className={styles.text}>{text}</p>
            ))}

            <div className={styles.sizes}>
              <span>Регистрации <b>{fmtNum(sizes.regs)}</b></span>
              <span>Активные <b>{fmtNum(sizes.active)}</b></span>
              <span>Платящих <b>{fmtNum(sizes.pays)}</b></span>
            </div>

            {demo.ages_known > 0 && (
              <>
                <div className={styles.blockTitle}>
                  Возраст {p.key === "minecraftia" ? "онлайн-активных" : "зарегистрированных"} (указали {fmtNum(demo.ages_known)})
                </div>
                {Object.entries(ages).map(([label, v]) => (
                  <StatBar key={label} label={label} value={v} max={maxAge} color={p.color} />
                ))}
              </>
            )}

            {p.key === "minecraftia" && demo.students_known > 0 && (
              <>
                <div className={styles.blockTitle}>Возраст учеников курсов ({fmtNum(demo.students_known)})</div>
                {Object.entries(demo.student_ages || {}).map(([label, v]) => (
                  <StatBar key={label} label={label} value={v}
                           max={Math.max(1, ...Object.values(demo.student_ages || {}))} color={p.color} />
                ))}
              </>
            )}

            <GenderLine gender={demo.gender} />
            <DevicesLine devices={demo.devices} />

            <div className={styles.caveat}>{p.caveat}</div>
          </Card>
        );
      })}
    </div>
  );
}
